// src/hooks/useAuth.js
// Supabase Auth + tier + TOS acceptance management.
// Exposes: tier, termsAccepted, refreshTier(), acceptTerms()
import { useState, useEffect, useCallback } from "react";
import { supabase, hasSupabase } from "../lib/supabaseClient";
import { clearAuthFetchCache } from "../lib/authFetch";
import { ensureOnboardingState } from "../lib/onboarding/onboardingService";
import { ensureUserPreferences } from "../lib/user/userService";

// fetchProfile — replaces fetchTier. Returns { tier, termsAccepted }.
// Error handling:
//   PGRST116 (row not found) → termsAccepted: false  (profile should exist, treat as unaccepted)
//   Any other error (network/timeout) → termsAccepted: null  (skip overlay to avoid lockout)
//
// betaAccess: reads from profiles.beta_access OR user_metadata.beta_access (set by
// consumeBetaCode via supabase.auth.updateUser). The metadata fallback bypasses the
// infinite-recursion bug in the profiles RLS policy.
async function fetchProfile(userId, user = null) {
  if (!hasSupabase || !userId) return { tier: "free", termsAccepted: null, betaAccess: false, isAdmin: false };

  // Always fetch fresh user data to pick up metadata changes (e.g. beta_access set
  // server-side via admin.updateUserById) without waiting for a JWT refresh cycle.
  let freshUser = user;
  try {
    const { data: { user: u } } = await supabase.auth.getUser();
    if (u) freshUser = u;
  } catch (_) { /* non-fatal */ }

  const { data, error } = await supabase
    .from("profiles")
    .select("tier, terms_accepted_at, beta_access, is_admin")
    .eq("id", userId)
    .single();
  if (error) {
    const isNotFound = error.code === "PGRST116";
    const metaBeta = !!freshUser?.user_metadata?.beta_access;
    return { tier: "free", termsAccepted: isNotFound ? false : null, betaAccess: metaBeta, isAdmin: false };
  }
  if (!data) return { tier: "free", termsAccepted: null, betaAccess: false, isAdmin: false };
  return {
    tier:          data.tier || "free",
    termsAccepted: !!data.terms_accepted_at,
    betaAccess:    !!(data.beta_access || freshUser?.user_metadata?.beta_access),
    isAdmin:       !!data.is_admin,
  };
}

// ensureProfile — creates a default profile row if none exists (step 1),
// then writes any pending terms acceptance from localStorage (step 2).
// Split into two operations to avoid ignoreDuplicates silently dropping
// the terms update on existing rows.
async function ensureProfile(userId) {
  if (!hasSupabase || !userId) return;
  const pending = localStorage.getItem("terms_pending_acceptance");

  // Step 1: INSERT default row (no-op if row already exists)
  const { error: insertError } = await supabase.from("profiles").upsert(
    { id: userId, tier: "free", updated_at: new Date().toISOString() },
    { onConflict: "id", ignoreDuplicates: true }
  );
  if (insertError) console.warn("[useAuth] ensureProfile insert failed:", insertError.message);

  // Step 2: Write terms acceptance timestamp if registration flag is present
  if (pending) {
    const { error: updateError } = await supabase
      .from("profiles")
      .update({ terms_accepted_at: pending, updated_at: new Date().toISOString() })
      .eq("id", userId)
      .is("terms_accepted_at", null); // guard: only set if not already accepted
    if (!updateError) localStorage.removeItem("terms_pending_acceptance");
    else console.warn("[useAuth] ensureProfile terms update failed:", updateError.message);
  }
}

export function useAuth() {
  const [user,                setUser]                = useState(null);
  const [session,             setSession]             = useState(null);
  const [tier,                setTier]                = useState("free");
  const [termsAccepted,       setTermsAccepted]       = useState(null); // null=loading, true=accepted, false=pending
  const [betaAccess,          setBetaAccess]          = useState(false);
  const [isAdmin,             setIsAdmin]             = useState(false);
  const [authLoading,         setAuthLoading]         = useState(true);
  // authReady: true as soon as the first onAuthStateChange event fires (session confirmed
  // either way). This is set BEFORE the slow ensure*/fetchProfile DB calls so dependent
  // hooks can start their own fetches without waiting for the full authLoading cycle.
  const [authReady,           setAuthReady]           = useState(false);
  const [authError,           setAuthError]           = useState(null);
  const [isPasswordRecovery,  setIsPasswordRecovery]  = useState(false);

  // refreshTier — called by BillingPage after Stripe subscription events.
  // Now also refreshes termsAccepted so billing flow doesn't clobber state.
  const refreshTier = useCallback(async () => {
    if (!hasSupabase || !supabase) return;
    try {
      const { data: { session: s } } = await supabase.auth.getSession();
      if (!s?.user) return;
      const { data: profile, error } = await supabase
        .from("profiles")
        .select("tier, terms_accepted_at")
        .eq("id", s.user.id)
        .single();
      if (!error) {
        setTier(profile?.tier || "free");
        setTermsAccepted(!!profile?.terms_accepted_at);
      }
    } catch (err) {
      console.warn("[useAuth] refreshTier failed", err);
    }
  }, []);

  // acceptTerms — called when authenticated user clicks I ACCEPT on the overlay.
  const acceptTerms = useCallback(async () => {
    if (!hasSupabase || !user) return;
    const now = new Date().toISOString();
    const { error } = await supabase
      .from("profiles")
      .update({ terms_accepted_at: now, updated_at: now })
      .eq("id", user.id);
    if (!error) setTermsAccepted(true);
    else console.warn("[useAuth] acceptTerms failed:", error.message);
  }, [user]);

  useEffect(() => {
    if (!hasSupabase) {
      setAuthReady(true);
      setAuthLoading(false);
      return;
    }

    // Safety net: never hang on the loading screen longer than 8 seconds.
    const loadingTimeout = setTimeout(() => setAuthLoading(false), 8000);

    // Session-level guard: ensure* calls are upserts that no-op for existing users.
    // Running them on every refresh wastes 3 sequential DB roundtrips. Skip if we
    // already ran them for this user in this browser session.
    const INIT_KEY = "nuvport_session_inited";
    function needsEnsure(userId) {
      return sessionStorage.getItem(INIT_KEY) !== userId;
    }
    function markEnsured(userId) {
      sessionStorage.setItem(INIT_KEY, userId);
    }

    // getSession() resolves the current session synchronously from the JWT in
    // localStorage. We only use it to set initial state — ensure* and fetchProfile
    // are handled entirely by onAuthStateChange below to avoid running them twice.
    supabase.auth.getSession().then(({ data: { session } }) => {
      // Only set state here if onAuthStateChange hasn't already fired.
      // onAuthStateChange fires with INITIAL_SESSION almost immediately after
      // getSession resolves, so this is mainly a fallback for the no-session case.
      setSession(prev => prev ?? session);
      setUser(prev => prev ?? (session?.user ?? null));
    }).catch(() => {
      clearTimeout(loadingTimeout);
      setAuthLoading(false);
    });

    // onAuthStateChange is the single source of truth for auth state.
    // ensure* only runs when the user genuinely hasn't been initialised this session.
    // authReady is set AFTER ensure* so dependent hooks don't race against upserts.
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (event === "PASSWORD_RECOVERY") setIsPasswordRecovery(true);
        if (event === "SIGNED_OUT") {
          setIsPasswordRecovery(false);
          sessionStorage.removeItem(INIT_KEY);
        }

        setSession(session);
        setUser(session?.user ?? null);
        // Set authReady immediately so dependent hooks (useUserPreferences,
        // onboarding guard) can start their reads right away. ensure* calls
        // below are upserts that no-op for existing rows, so reads racing them
        // is safe for returning users. New signups have rows created by the
        // DB trigger before SIGNED_IN fires.
        setAuthReady(true);

        if (session?.user) {
          const isNewSession = event === "SIGNED_IN" || event === "INITIAL_SESSION";
          if (isNewSession && needsEnsure(session.user.id)) {
            await ensureProfile(session.user.id);
            await ensureOnboardingState(session.user.id);
            await ensureUserPreferences(session.user.id);
            markEnsured(session.user.id);
          }
          const profile = await fetchProfile(session.user.id, session.user);
          setTier(profile.tier);
          setTermsAccepted(profile.termsAccepted);
          setBetaAccess(profile.betaAccess);
          setIsAdmin(profile.isAdmin);
        } else {
          setTier("free");
          setTermsAccepted(null);
          setBetaAccess(false);
          setIsAdmin(false);
        }

        clearTimeout(loadingTimeout);
        setAuthLoading(false);
      }
    );

    return () => { subscription.unsubscribe(); clearTimeout(loadingTimeout); };
  }, []); // no loadTier dependency — loadTier removed

  const clearError = useCallback(() => setAuthError(null), []);

  const signIn = useCallback(async (email, password) => {
    if (!hasSupabase) { setAuthError("Supabase is not configured."); return false; }
    setAuthError(null);
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) { setAuthError(error.message); return false; }
    return true;
  }, []);

  const signUp = useCallback(async (email, password) => {
    if (!hasSupabase) { setAuthError("Supabase is not configured."); return false; }
    setAuthError(null);
    const { error } = await supabase.auth.signUp({ email, password });
    if (error) { setAuthError(error.message); return false; }
    return true;
  }, []);

  const resetPassword = useCallback(async (email) => {
    if (!hasSupabase) return false;
    setAuthError(null);
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: window.location.origin,
    });
    if (error) { setAuthError(error.message); return false; }
    return true;
  }, []);

  const updatePassword = useCallback(async (newPassword) => {
    setAuthError(null);
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) { setAuthError(error.message); return false; }
    setIsPasswordRecovery(false);
    return true;
  }, []);

  const signInWithGoogle = useCallback(async () => {
    if (!hasSupabase) { setAuthError("Supabase is not configured."); return; }
    setAuthError(null);
    await supabase.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo: window.location.origin },
    });
  }, []);

  // signOut clears the terms_pending_acceptance flag to prevent a stale
  // flag from being applied to the next user who signs in on this device.
  // window.location.href forces a full JS runtime reset, destroying all
  // closures (including the supabaseUserId captured in usePersistence) so
  // a second user cannot inherit the previous user's debounced writes.
  const signOut = useCallback(() => {
    clearAuthFetchCache();
    localStorage.removeItem("terms_pending_acceptance");
    // Clear Supabase session from localStorage directly — supabase.auth.signOut()
    // hangs when the refresh-token network call stalls, so we bypass it entirely.
    try {
      const projectRef = (process.env.REACT_APP_SUPABASE_URL ?? "")
        .match(/https:\/\/([^.]+)\.supabase\.co/)?.[1] ?? "";
      // Clear by project ref if available, otherwise nuke all sb- keys as fallback
      Object.keys(localStorage)
        .filter(k => projectRef ? k.startsWith(`sb-${projectRef}-`) : k.startsWith("sb-"))
        .forEach(k => localStorage.removeItem(k));
    } catch {}
    window.location.href = "/";
  }, []);

  return {
    user,
    session,
    tier,
    termsAccepted,
    betaAccess,
    isAdmin,
    grantBetaAccess: () => setBetaAccess(true),
    authLoading,
    authReady,
    authError,
    clearError,
    signIn,
    signUp,
    signInWithGoogle,
    signOut,
    resetPassword,
    updatePassword,
    isPasswordRecovery,
    setTier,
    refreshTier,
    acceptTerms,
    hasSupabase,
  };
}
